import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/**
 * 核心工具：直接操作 docx 文件内部的 XML，实现 100% 格式保留的变量替换
 *
 * 修复说明（2026-06）：
 * 1. Word 会把 {{变量名}} 拆到多个 <w:r> 块里，旧代码按 <w:r> 逐个处理，
 *    导致变量永远匹配不上。现已改为：先归一化（把跨 <w:r> 的变量合并到同一个 <w:t>），
 *    再做简单替换。
 * 2. 归一化逻辑：扫描全部 <w:t> 标签，找到跨标签的 {{}}，把内容合并到第一个 <w:t>，
 *    其余 <w:t> 清空，这样后续替换就能正确工作。
 */

export interface ReplaceVariables {
  [key: string]: string;
}

export interface TableItem {
  [key: string]: string;
}

/**
 * 从 docx 文件的 ArrayBuffer 中直接替换变量，生成新的 docx 文件
 */
export async function generateContract(
  templateBuffer: ArrayBuffer,
  variables: ReplaceVariables,
  tableItems?: TableItem[],
  tableRowVariables?: string[]
): Promise<Blob> {
  const zip = await JSZip.loadAsync(templateBuffer);

  const documentXml = zip.file('word/document.xml');
  if (documentXml) {
    let xmlContent = await documentXml.async('text');

    // 关键修复：先归一化，把被 Word 拆到多个 <w:r> 的变量合并回同一个 <w:t>
    xmlContent = normalizeSplitVariables(xmlContent);

    // 归一化后，所有 {{var}} 都在单个 <w:t> 里，直接做字符串替换
    xmlContent = replaceAllVariables(xmlContent, variables);

    // 处理表格明细行
    if (tableItems && tableItems.length > 1 && tableRowVariables && tableRowVariables.length > 0) {
      xmlContent = replaceTableRowsInXml(xmlContent, tableItems, tableRowVariables);
    }

    zip.file('word/document.xml', xmlContent);
  }

  // 处理页眉
  await replaceInPart(zip, 'word/header', variables);
  // 处理页脚
  await replaceInPart(zip, 'word/footer', variables);

  // 处理其他 XML parts（批注等）
  const allFiles = Object.keys(zip.files);
  for (const filePath of allFiles) {
    if (
      filePath === 'word/document.xml' ||
      filePath.startsWith('word/header') ||
      filePath.startsWith('word/footer')
    ) {
      continue;
    }
    if (filePath.endsWith('.xml') || filePath.endsWith('.xml.rels')) {
      let content = await zip.files[filePath].async('text');
      const originalContent = content;
      content = replaceAllVariables(content, variables);
      if (content !== originalContent) {
        zip.file(filePath, content);
      }
    }
  }

  const newDocxBlob = await zip.generateAsync(
    {
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 },
    },
    (metadata) => {
      console.log(`生成进度: ${metadata.percent.toFixed(0)}%`);
    }
  );

  return newDocxBlob;
}

/**
 * 归一化 XML：把被 Word 拆分到多个 <w:t> 标签里的 {{变量名}} 合并回同一个 <w:t>
 *
 * Word 在用户输入 {{变量名}} 后，如果进行了拼写检查、格式调整等操作，
 * 可能会把同一个 run 里的文本拆成多个 <w:t> 标签，甚至拆到多个 <w:r> 块里。
 * 这个函数找出所有被拆分的 {{}}，把内容合并到第一个 <w:t>，其余清空。
 */
function normalizeSplitVariables(xml: string): string {
  // 1. 找出所有 <w:t> 标签的位置和文本内容
  const tTagRegex = /<w:t[^>]*>([\s\S]*?)<\/w:t>/g;
  const tTags: {
    fullMatch: string;
    content: string;
    start: number;
    end: number;
  }[] = [];

  let match: RegExpExecArray | null;
  while ((match = tTagRegex.exec(xml)) !== null) {
    tTags.push({
      fullMatch: match[0],
      content: match[1],
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  if (tTags.length === 0) return xml;

  // 2. 拼接全部 <w:t> 的文本内容，建立字符位置 → <w:t> 标签的映射
  let fullText = '';
  const charToTag: { charIndex: number; tagIndex: number }[] = [];
  for (let i = 0; i < tTags.length; i++) {
    const tag = tTags[i];
    for (let j = 0; j < tag.content.length; j++) {
      charToTag.push({ charIndex: fullText.length + j, tagIndex: i });
    }
    fullText += tag.content;
  }

  // 3. 在拼接后的文本里找所有 {{var}}
  const varRegex = /\{\{([^}]+)\}\}/g;
  const merges: {
    varName: string;
    startCharIdx: number;
    endCharIdx: number;
    startTagIdx: number;
    endTagIdx: number;
  }[] = [];

  let varMatch: RegExpExecArray | null;
  while ((varMatch = varRegex.exec(fullText)) !== null) {
    const varName = varMatch[1];
    const varStart = varMatch.index;
    const varEnd = varMatch.index + varMatch[0].length;

    // 找到包含 varStart 和 varEnd 的 <w:t> 标签
    const startTagIdx = findTagIndex(charToTag, varStart);
    const endTagIdx = findTagIndex(charToTag, varEnd - 1);

    if (startTagIdx !== -1 && endTagIdx !== -1 && startTagIdx !== endTagIdx) {
      merges.push({
        varName,
        startCharIdx: varStart,
        endCharIdx: varEnd,
        startTagIdx,
        endTagIdx,
      });
    }
  }

  if (merges.length === 0) return xml;

  // 4. 执行合并：把涉及多个 <w:t> 的变量合并到第一个 <w:t>
  // 从后往前处理，避免位置偏移
  merges.sort((a, b) => b.startTagIdx - a.startTagIdx);

  let result = xml;

  // 收集所有需要清空的标签索引
  const tagsToEmpty = new Set<number>();
  const firstTagNewContent = new Map<number, string>();

  for (const m of merges) {
    // 拼接这个变量涉及的所有 <w:t> 的内容
    const mergedContent = tTags
      .slice(m.startTagIdx, m.endTagIdx + 1)
      .map(t => t.content)
      .join('');

    firstTagNewContent.set(m.startTagIdx, mergedContent);

    for (let i = m.startTagIdx + 1; i <= m.endTagIdx; i++) {
      tagsToEmpty.add(i);
    }
  }

  // 从后往前替换，避免位置偏移
  const allIndices = Array.from(
    new Set([
      ...Array.from(firstTagNewContent.keys()),
      ...Array.from(tagsToEmpty),
    ])
  ).sort((a, b) => b - a);

  for (const idx of allIndices) {
    const tag = tTags[idx];
    const encoded: string = encodeXml(
      firstTagNewContent.has(idx) ? firstTagNewContent.get(idx)! : ''
    );

    // 重建这个 <w:t> 标签
    const newTag = tag.fullMatch.replace(
      /(<w:t[^>]*>)([\s\S]*?)(<\/w:t>)/,
      `$1${encoded}$3`
    );

    result = result.substring(0, tag.start) + newTag + result.substring(tag.end);

    // 更新后续 tTags 的位置（因为当前替换可能改变了长度）
    const delta = newTag.length - tag.fullMatch.length;
    for (let i = idx + 1; i < tTags.length; i++) {
      if (tTags[i].start > tag.start) {
        tTags[i].start += delta;
        tTags[i].end += delta;
      }
    }
  }

  return result;
}

function findTagIndex(charToTag: { charIndex: number; tagIndex: number }[], charIdx: number): number {
  // charToTag 按 charIndex 排序，二分查找
  let lo = 0, hi = charToTag.length - 1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (charToTag[mid].charIndex <= charIdx) {
      if (mid === charToTag.length - 1 || charToTag[mid + 1].charIndex > charIdx) {
        return charToTag[mid].tagIndex;
      }
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return -1;
}

/**
 * 在 XML 中替换所有变量（假设所有 {{var}} 都已在单个 <w:t> 内）
 * 使用全局字符串替换，简单可靠。
 */
function replaceAllVariables(xml: string, variables: ReplaceVariables): string {
  // 按变量名长度降序，避免短变量名误匹配长变量名的一部分
  const sortedNames = Object.keys(variables).sort((a, b) => b.length - a.length);

  let result = xml;
  for (const name of sortedNames) {
    const placeholder = `{{${name}}}`;
    const value = variables[name] || '';
    // 全局替换，replace 的 string 模式只替换第一个，要用 regex
    const regex = new RegExp(escapeRegex(placeholder), 'g');
    result = result.replace(regex, () => encodeXml(value));
  }

  return result;
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 在 Word XML 表格中动态替换明细行
 */
function replaceTableRowsInXml(
  xmlContent: string,
  items: TableItem[],
  rowVariables: string[]
): string {
  if (items.length <= 1) return xmlContent;

  // 找到所有 <w:tr> 行
  const trRegex = /<w:tr[\s\S]*?<\/w:tr>/g;
  const allRows: { match: string; index: number }[] = [];
  let trMatch: RegExpExecArray | null;
  while ((trMatch = trRegex.exec(xmlContent)) !== null) {
    allRows.push({ match: trMatch[0], index: trMatch.index });
  }

  // 找出哪些行包含表格变量（数据行）
  const dataRowIndices: number[] = [];
  for (let i = 0; i < allRows.length; i++) {
    for (const v of rowVariables) {
      if (allRows[i].match.includes(`{{${v}}}`)) {
        dataRowIndices.push(i);
        break;
      }
    }
  }

  if (dataRowIndices.length === 0) return xmlContent;

  const templateRow = allRows[dataRowIndices[0]].match;

  // 为每行数据生成 XML
  const newRowsXml: string[] = [];
  for (const item of items) {
    let rowXml = templateRow;
    for (const v of rowVariables) {
      const placeholder = `{{${v}}}`;
      const value = item[v] || '';
      rowXml = rowXml.split(placeholder).join(encodeXml(value));
    }
    newRowsXml.push(rowXml);
  }

  // 替换：保留第一个数据行之前的内容 + 新行 + 最后一个数据行之后的内容
  const firstIdx = allRows[dataRowIndices[0]].index;
  const lastRow = allRows[dataRowIndices[dataRowIndices.length - 1]];
  const lastEnd = lastRow.index + lastRow.match.length;

  return xmlContent.substring(0, firstIdx) + newRowsXml.join('') + xmlContent.substring(lastEnd);
}

/**
 * 处理文档部件（页眉/页脚等）
 */
async function replaceInPart(
  zip: JSZip,
  partPrefix: string,
  variables: ReplaceVariables
): Promise<void> {
  const files = Object.keys(zip.files).filter(
    (name) => name.startsWith(partPrefix) && name.endsWith('.xml')
  );

  for (const filePath of files) {
    let content = await zip.files[filePath].async('text');
    const originalContent = content;
    content = normalizeSplitVariables(content);
    content = replaceAllVariables(content, variables);
    if (content !== originalContent) {
      zip.file(filePath, content);
    }
  }
}

/**
 * 从 docx 模板中提取所有变量占位符
 */
export async function extractVariablesFromTemplate(
  templateBuffer: ArrayBuffer
): Promise<string[]> {
  const zip = await JSZip.loadAsync(templateBuffer);
  const variables = new Set<string>();

  const documentXml = zip.file('word/document.xml');
  if (documentXml) {
    const xmlContent = await documentXml.async('text');
    extractVariablesFromXml(xmlContent, variables);
  }

  const allFiles = Object.keys(zip.files);
  for (const filePath of allFiles) {
    if (
      (filePath.startsWith('word/header') || filePath.startsWith('word/footer')) &&
      filePath.endsWith('.xml')
    ) {
      const content = await zip.files[filePath].async('text');
      extractVariablesFromXml(content, variables);
    }
  }

  return Array.from(variables);
}

/**
 * 从 XML 内容中提取变量名
 * 拼接整个 XML 中所有 <w:t> 文本，再提取 {{}}
 */
function extractVariablesFromXml(xmlContent: string, variables: Set<string>): void {
  let fullText = '';
  const textTagsRegex = /<w:t[^>]*>([\s\S]*?)<\/w:t>/g;
  let textMatch: RegExpExecArray | null;
  while ((textMatch = textTagsRegex.exec(xmlContent)) !== null) {
    fullText += textMatch[1];
  }

  const varRegex = /\{\{([^}]+)\}\}/g;
  let varMatch: RegExpExecArray | null;
  while ((varMatch = varRegex.exec(fullText)) !== null) {
    variables.add(varMatch[1]);
  }
}

function encodeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * 下载 Blob 为文件
 */
export function downloadBlob(blob: Blob, filename: string): void {
  saveAs(blob, filename);
}
